import {
  calculateUploadProgress,
  endpointsList,
  getTotalFormDataSize,
  toFormData,
} from '@/helpers';
import axios, { AxiosProgressEvent } from 'axios';
import {
  DocumentKind,
  IDocumentDto,
  IDocumentsUploadDto,
  IFileUploadDto,
  Serialized,
} from 'types';
import { isDefined } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import { computed, Ref, ref } from 'vue';
import {
  FileUploadStatus,
  IFileUploadStatuses,
  IUploadRecord,
} from '../MassUpload.types';
import { useToast } from '@/composables/useToast';
import { useI18n } from 'vue-i18n';

type FormDataForUpload = {
  documentId: string;
  file: FormData;
  renditionFile?: FormData;
};

export function useMassUpload(
  uploadRecords: Ref<IUploadRecord[]>,
  projectId: string,
  supplierId: string | undefined,
  referenceNumber: string,
  contentTypeId: string,
  checkBotConfigurationId: string | undefined,
  isCheckBotInspectionDisabled: Ref<boolean>,
  skippedFiles?: Ref<string[]>,
) {
  const uploadCorrelationId = uuidv4();
  const fileUploadStatuses = ref<IFileUploadStatuses>({});
  const totalFileSize = ref(0);
  const remainingFileSize = ref(0);
  const { createErrorToast } = useToast();
  const { t } = useI18n();

  const uploadProgress = computed(() =>
    calculateUploadProgress(totalFileSize.value, remainingFileSize.value),
  );

  async function uploadFilesAndMetadata(): Promise<boolean> {
    const hasUploadableFiles = uploadRecords.value.some(
      (record) =>
        !record.duplicate &&
        !record.renditionFile?.duplicate &&
        record.file?.size,
    );
    if (!hasUploadableFiles) {
      createErrorToast(t('documentUpload.errors.noUploadableFiles'));
      return false;
    }
    await uploadFiles();
    await uploadMetadata();
    return true;
  }

  async function uploadFiles(): Promise<void> {
    const formData = transformUploadRecordsToFormData();
    await setTotalFileSize(formData);

    for (const data of formData) {
      fileUploadStatuses.value[data.documentId] = FileUploadStatus.UPLOADING;
      await Promise.all(
        [data.file, data.renditionFile].map(async (data) => {
          /** Rendition file can be undefined */
          if (!data) return;
          await axios.post(endpointsList.uploadHandler.uploadFile(), data, {
            onUploadProgress: (e: AxiosProgressEvent) => {
              remainingFileSize.value = remainingFileSize.value - e.bytes;
            },
          });
        }),
      );
      fileUploadStatuses.value[data.documentId] = FileUploadStatus.UPLOADED;
    }
  }

  /**
   * Transforming all upload records with files to form data before starting
   * the upload to be able to calculate the total data size to be uploaded.
   */
  function transformUploadRecordsToFormData(): FormDataForUpload[] {
    return uploadRecords.value
      .filter((record) => !record.duplicate && !record.renditionFile?.duplicate)
      .map((record) => {
        /** Skipping records without files (just metadata changes) */
        if (!record.file?.size) {
          return;
        }

        const [file, renditionFile] = [record, record.renditionFile].map(
          (record, index) => {
            /** The rendition file is optional, so we check if it exists */
            if (!record?.file?.size) return;
            return toFormData<IFileUploadDto & { file: File }>({
              uploadCorrelationId: uploadCorrelationId,
              documentId: record.metadata!.documentId,
              file: record.file,
              documentType:
                index === 0 ? DocumentKind.MAIN : DocumentKind.RENDITION,
            });
          },
        );
        return {
          documentId: record.metadata!.documentId,
          file: file!,
          renditionFile: renditionFile,
        };
      })
      .filter(isDefined);
  }

  /**
   * Calculating the total file size of all files to be uploaded for tracking
   * upload progress.
   */
  async function setTotalFileSize(
    formData: FormDataForUpload[],
  ): Promise<void> {
    const formDataArray = formData
      .map((data) => [data.file, data.renditionFile].filter(isDefined))
      .flat();
    const totalSize = await getTotalFormDataSize(formDataArray);
    totalFileSize.value = totalSize;
    remainingFileSize.value = totalSize;
  }

  async function uploadMetadata(): Promise<void> {
    const files: IDocumentDto[] = uploadRecords.value
      .filter((record) => !record.duplicate && !record.renditionFile?.duplicate)
      .map((record) => ({
        fileMetadata: record.metadata!,
        rawMetadata: record.rawMetadata,
        isRenditionFile: !!record.renditionFile,
      }));
    const documentsUpload: Serialized<IDocumentsUploadDto> = {
      uploadCorrelationId,
      referenceNumber,
      contentTypeId,
      checkBotConfigurationId,
      isCheckBotInspectionDisabled: isCheckBotInspectionDisabled.value,
      supplierId,
      projectId,
      files,
      skippedFiles: skippedFiles?.value,
    };
    await axios.post(
      endpointsList.uploadHandler.processUpload(),
      documentsUpload,
    );
  }

  return { uploadFilesAndMetadata, fileUploadStatuses, uploadProgress };
}
